import type { ApiError } from '@bb/shared';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
    /**
     * The API's own sentence, when it has one worth showing.
     *
     * Added for the LINE wizard, where the useful part is often what LINE said
     * back — "the channel secret is invalid" tells a shop which box to look in,
     * and it was being thrown away here in favour of the code alone.
     */
    public readonly serverMessage?: string,
  ) {
    super(code);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Extra request headers. Added for the LINE MINI App's booker token, which
   * travels in a header rather than the query string so it cannot be left in a
   * server log or a referrer.
   */
  headers?: Record<string, string>;
}

/**
 * Thin fetch wrapper. Cookies carry the session; the custom header is part of
 * the CSRF defence (browsers won't send it cross-site without a preflight).
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers } = options;
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'fetch',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiRequestError(0, 'network');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiError>;
    throw new ApiRequestError(
      response.status,
      error.code ?? 'generic',
      error.details,
      error.message,
    );
  }
  return payload as T;
}
